import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Badge, Button, Select } from '@atoms/index';
import { DateRangePicker } from '@molecules/index';
import { useRestaurantStore } from '@store/restaurantStore';
import type { OrderFilters } from '@services/restaurantOrderService';
import { AssignCourierModal } from '@organisms/index';
import type { Order, OrderStatus } from '@/types';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/currency';
import { formatPaymentMethod } from '@/utils/formatters';

type OrdersTabProps = {
  socketConnected: boolean;
  newOrderIds?: ReadonlySet<string>;
  onSelectOrderId: (orderId: string) => void;
};

const OrdersTab = ({
  socketConnected,
  newOrderIds,
  onSelectOrderId,
}: OrdersTabProps) => {
  const {
    orders,
    isLoadingOrders,
    error,
    loadOrders,
    updateOrderStatus,
    clearError,
  } = useRestaurantStore();

  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | 'ALL' | 'NEW'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [assignCourierOrder, setAssignCourierOrder] = useState<Order | null>(null);
  const [assignSuccessMessage, setAssignSuccessMessage] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const orderListQueryFilters = useMemo((): OrderFilters => {
    const filters: OrderFilters = {};
    if (selectedStatus !== 'ALL') {
      filters.status = selectedStatus as OrderStatus;
    }
    if (startDate) {
      filters.startDate = new Date(startDate).toISOString();
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filters.endDate = end.toISOString();
    }
    return filters;
  }, [selectedStatus, startDate, endDate]);

  useEffect(() => {
    void loadOrders(orderListQueryFilters);
  }, [orderListQueryFilters, loadOrders]);

  const handleRefreshOrders = useCallback(() => {
    void loadOrders(orderListQueryFilters);
  }, [loadOrders, orderListQueryFilters]);

  const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
    } catch (err) {
      console.error('Update order status error:', err);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.orderNumber?.toLowerCase().includes(query) ||
      order.items.some((item) =>
        item.menuItemName.toLowerCase().includes(query),
      )
    );
  });

  const newOrderStatuses: OrderStatus[] = ['CREATED', 'SCHEDULED', 'PENDING'];
  const ordersByStatus = {
    CREATED: filteredOrders.filter((o) => newOrderStatuses.includes(o.status || '')),
    PREPARING: filteredOrders.filter((o) => o.status === 'PREPARING'),
    READY: filteredOrders.filter((o) => o.status === 'READY'),
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Siparişler</h2>
          <p className="text-gray-600">Siparişleri yönetin ve durumlarını güncelleyin</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            isLoading={isLoadingOrders}
            disabled={isLoadingOrders}
            onClick={handleRefreshOrders}
            className="shrink-0 gap-2 rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50"
            title="Seçili filtrelere göre sipariş listesini sunucudan yeniden yükler"
            aria-label="Sipariş listesini yenile"
          >
            {!isLoadingOrders && (
              <svg
                className="h-4 w-4 shrink-0 text-[#E63946]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
            )}
            Listeyi yenile
          </Button>
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'w-3 h-3 rounded-full',
                socketConnected ? 'bg-green-500' : 'bg-red-500',
              )}
            />
            <span className="text-sm text-gray-600">
              {socketConnected ? 'Bağlı' : 'Bağlantı Kesildi'}
            </span>
          </div>
        </div>
      </div>

      <Card>
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Select
                label="Durum"
                value={selectedStatus}
                onChange={(value) => setSelectedStatus(value as OrderStatus | 'ALL' | 'NEW')}
                options={[
                  { value: 'ALL', label: 'Tümü' },
                  { value: 'NEW', label: 'Yeni' },
                  { value: 'PREPARING', label: 'Hazırlanıyor' },
                  { value: 'READY', label: 'Hazır' },
                ]}
                size="md"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ara
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Sipariş numarası veya ürün adı..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
            />
          </div>
        </div>
      </Card>

      {assignSuccessMessage && (
        <Card className="bg-success/10 border border-success/20">
          <p className="text-success">{assignSuccessMessage}</p>
        </Card>
      )}

      {error && (
        <Card className="bg-error/10 border border-error/20">
          <p className="text-error">{error}</p>
          <Button size="sm" variant="primary" onClick={clearError} className="mt-2">
            Kapat
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-primary">
              {ordersByStatus.CREATED.length}
            </p>
            <p className="text-sm text-gray-600">Yeni Siparişler</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-warning">
              {ordersByStatus.PREPARING.length}
            </p>
            <p className="text-sm text-gray-600">Hazırlanıyor</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-success">
              {ordersByStatus.READY.length}
            </p>
            <p className="text-sm text-gray-600">Hazır</p>
          </div>
        </Card>
      </div>

      {isLoadingOrders && (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Siparişler yükleniyor...</p>
        </div>
      )}

      {!isLoadingOrders && (
        <div className="grid grid-cols-3 gap-6">
          <KanbanColumn
            title="Yeni Siparişler"
            subtitle="Onay Bekliyor"
            color="primary"
            orders={ordersByStatus.CREATED}
            onStatusChange={handleStatusChange}
            onOrderClick={onSelectOrderId}
            onAssignCourier={setAssignCourierOrder}
            nextStatus="PREPARING"
            newOrderIds={newOrderIds}
          />
          <KanbanColumn
            title="Hazırlanıyor"
            subtitle="Mutfakta"
            color="warning"
            orders={ordersByStatus.PREPARING}
            onStatusChange={handleStatusChange}
            onOrderClick={onSelectOrderId}
            onAssignCourier={setAssignCourierOrder}
            nextStatus="READY"
            newOrderIds={newOrderIds}
          />
          <KanbanColumn
            title="Hazır"
            subtitle="Kurye Bekliyor"
            color="success"
            orders={ordersByStatus.READY}
            onStatusChange={handleStatusChange}
            onOrderClick={onSelectOrderId}
            onAssignCourier={setAssignCourierOrder}
            nextStatus={null}
            newOrderIds={newOrderIds}
          />
        </div>
      )}

      {assignCourierOrder && (
        <AssignCourierModal
          orderId={assignCourierOrder.id}
          orderNumber={assignCourierOrder.orderNumber || assignCourierOrder.id.slice(0, 8)}
          isOpen={Boolean(assignCourierOrder)}
          onClose={() => setAssignCourierOrder(null)}
          onAssigned={() => {
            setAssignSuccessMessage('Kurye başarıyla atandı.');
            setAssignCourierOrder(null);
            handleRefreshOrders();
            setTimeout(() => setAssignSuccessMessage(null), 4000);
          }}
        />
      )}
    </div>
  );
};

interface KanbanColumnProps {
  title: string;
  subtitle: string;
  color: 'primary' | 'warning' | 'success';
  orders: Order[];
  onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
  onOrderClick: (orderId: string) => void;
  onAssignCourier: (order: Order) => void;
  nextStatus: OrderStatus | null;
  newOrderIds?: ReadonlySet<string>;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  title,
  subtitle,
  color,
  orders,
  onStatusChange,
  onOrderClick,
  onAssignCourier,
  nextStatus,
  newOrderIds,
}) => {
  const colorClasses = {
    primary: 'border-primary bg-primary/5',
    warning: 'border-warning bg-warning/5',
    success: 'border-success bg-success/5',
  };

  const badgeColors = {
    primary: 'info',
    warning: 'warning',
    success: 'success',
  } as const;

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          'border-2 rounded-t-lg p-4',
          colorClasses[color],
        )}
      >
        <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600">{subtitle}</p>
        <div className="mt-2">
          <Badge variant={badgeColors[color]}>{orders.length} Sipariş</Badge>
        </div>
      </div>

      <div className="flex-1 border-x-2 border-b-2 border-gray-200 rounded-b-lg p-4 space-y-3 min-h-[400px]">
        {orders.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>Sipariş yok</p>
          </div>
        ) : (
          orders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onStatusChange={onStatusChange}
              onOrderClick={onOrderClick}
              onAssignCourier={onAssignCourier}
              nextStatus={nextStatus}
              isNew={newOrderIds?.has(order.id) ?? false}
            />
          ))
        )}
      </div>
    </div>
  );
};

interface OrderCardProps {
  order: Order;
  onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
  onOrderClick: (orderId: string) => void;
  onAssignCourier: (order: Order) => void;
  nextStatus: OrderStatus | null;
  isNew?: boolean;
}

const OrderCard: React.FC<OrderCardProps> = ({
  order,
  onStatusChange,
  onOrderClick,
  onAssignCourier,
  nextStatus,
  isNew,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const canAssignCourier =
    !order.courierId && (order.status === 'PREPARING' || order.status === 'READY');

  return (
    <Card
      className={cn(
        'hover:shadow-md transition-all cursor-pointer',
        isNew && 'ring-2 ring-[#E63946] ring-offset-1 animate-pulse-once',
      )}
      onClick={() => onOrderClick(order.id)}
    >
      <div className="space-y-3">
        {isNew && (
          <div className="flex items-center gap-1.5 -mt-1 mb-0.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E63946] text-white text-[10px] font-bold uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-ping" />
              Yeni Sipariş
            </span>
          </div>
        )}
        <div className="flex items-start justify-between">
          <div>
            <p className="font-bold text-gray-900">
              Sipariş #{order.orderNumber || order.id.slice(0, 8)}
            </p>
            <p className="text-sm text-gray-500">
              {new Date(order.createdAt).toLocaleTimeString('tr-TR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={order.paymentStatus === 'PAID' ? 'success' : 'warning'}>
              {order.paymentStatus === 'PAID' ? 'Ödendi' : 'Bekliyor'}
            </Badge>
            {order.paymentMethod && (
              <Badge variant="info" className="text-xs">
                {formatPaymentMethod(order.paymentMethod)}
              </Badge>
            )}
          </div>
        </div>

        <div className="text-sm text-gray-600">
          <p className="font-medium">{order.items.length} Ürün</p>
          {!isExpanded && order.items.length > 0 && (
            <p className="truncate">{order.items[0].menuItemName}</p>
          )}
        </div>

        {isExpanded && (
          <div className="space-y-2 pt-2 border-t">
            {order.items.map((item, idx) => (
              <div key={idx} className="text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">
                    {item.quantity}x {item.menuItemName}
                  </span>
                  <span className="font-medium">{formatCurrency(item.unitPrice)}</span>
                </div>
                {item.optionNames && item.optionNames.length > 0 && (
                  <div className="text-xs text-gray-600 mt-1 ml-4">
                    {item.optionNames.join(', ')}
                  </div>
                )}
                {item.removedIngredientNames && item.removedIngredientNames.length > 0 && (
                  <div className="text-xs text-red-600 mt-1 ml-4 font-medium">
                    Çıkarılan: {item.removedIngredientNames.join(', ')}
                  </div>
                )}
                {item.specialInstructions && (
                  <div className="text-xs text-orange-600 italic mt-1 ml-4 bg-orange-50 p-1.5 rounded">
                    <span className="font-medium">Not: </span>
                    {item.specialInstructions}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {(order.customerNotes || (order as any).customer_notes) && (
          <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs font-semibold text-red-600 mb-0.5">Sipariş Notu</p>
            <p className="text-sm text-gray-800">{order.customerNotes || (order as any).customer_notes}</p>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <span className="font-bold text-lg text-gray-900">
            {formatCurrency(
              typeof order.total === 'number' && !isNaN(order.total)
                ? order.total
                : (order.subtotal || 0) + (order.deliveryFee || 0) + (order.serviceFee || 0) || 0
            )}
          </span>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="text-sm text-primary hover:underline"
          >
            {isExpanded ? 'Daralt' : 'Detay'}
          </button>
        </div>

        <div className="mt-2 flex flex-wrap justify-end gap-2">
          {canAssignCourier && (
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onAssignCourier(order);
              }}
            >
              🛵 Kurye Ata
            </Button>
          )}
          {nextStatus && (
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange(order.id, nextStatus);
              }}
            >
              {nextStatus === 'PREPARING' && '👨‍🍳 Hazırlamaya Başla'}
              {nextStatus === 'READY' && '✅ Hazır Olarak İşaretle'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default OrdersTab;
